import { ReactComponent as OutlinePlus16 } from "assets/svg/outline-plus-16.svg";
import CheckboxDropdown from "components/checkbox-dropdown/CheckboxDropdown";
import { useUser } from "contexts/UserContext";
import { usePhonePortraitContext } from "contexts/phone-portrait-context";
import { useSubscription } from "contexts/subscription-context";
import { useTeamContext } from "contexts/group-practice-context/TeamContext";
import { useCalendarAppointments } from "../../../../contexts/CalendarAppointmentsContext";
import useToggleModal from "hooks/useToggleModal";
import messages from "messages";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import {
  MobileSettingsScreenType,
  selectCurrentMobileSettingsScreen,
  showAvailabilityScreen,
  showCancellationPolicyScreen,
  showSessionTypesScreen,
} from "store/calendarSidebarSlice";
import { PRO_CALENDAR_ANALYTICS_PAGE_NAME, track } from "utils/amplitude";
import getClasses from "utils/getClasses";
import { useBlockedIntervals } from "../../../../contexts/BlockedIntervalsContext";
import { useSessionTypesEditor } from "../../../../contexts/SessionTypesEditorContext";
import CancellationPolicyManager from "../calendar-view/components/cancellation-policy-manager";
import GoogleCalendar from "../google-calendar/GoogleCalendar";
import getSelectedGCals from "services/gcal/getSelectedGCals";
import toggleGCalVisibility from "services/gcal/toggleGCalVisibility";
import ScheduleEditorContainer from "../calendar-view/components/schedule-editor-container/ScheduleEditorContainer";
import SelectGCalsModal from "../google-calendar/SelectGCalsModal";
import ICalCalendar from "../icalendar";
import styles from "./CalendarSidebar.module.scss";
import CancellationPolicyMobileScreen from "./cancellation-policy-mobile-screen/CancellationPolicyMobileScreen";
import AppointmentsToggle from "./components/appointments-toggle/AppointmentsToggle";
import AvailabilityMobileScreen from "./components/availability-mobile-screen/AvailabilityMobileScreen";
import BlockedIntervalsToggle from "./components/blocked-intervals-toggle/BlockedIntervalsToggle";
import CancelledSessionsToggle from "./components/cancelled-sessions-toggle/CancelledSessionsToggle";
// import GCalToggle from "./components/gcal-toggle";
import GroupSessionsToggle from "./components/group-sessions-toggle/GroupSessionsToggle";
import MiniCalendar from "./components/mini-calendar/MiniCalendar";
import Section from "./components/section";
import SessionTypesMobileScreen from "./components/session-types-mobile-screen/SessionTypesMobileScreen";
import TimeRangeSection from "./components/time-range-section/TimeRangeSection";
import SidesheetContainer from "./sidesheet-container/SidesheetContainer";
import ColorCheckbox from "../color-checkbox/ColorCheckbox";
import { useNavigate } from "react-router-dom";

function getGCalAccountName(user: ReturnType<typeof useUser>["user"]) {
  if (!user) {
    return "";
  }
  const strings = [user.first_name, user.last_name].filter((el) => el);
  if (strings.length === 0) {
    return `(${messages.noTitle})`;
  }
  return strings.join(" ");
}

const ANALYTICS_COMPONENT_NAME = "cal-sidebar";

const formatRoleName = (role: string): string => {
  return role
    .replace(/_/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const CalendarSidebar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useUser();
  const { isPhonePortrait } = usePhonePortraitContext();
  const { practiceOwnerOrchidUserId, canAccessFeatureByRole, teamMembers } =
    useTeamContext();
  const { visibleTeamMemberIds, setVisibleTeamMemberIds } =
    useCalendarAppointments();

  const { openSessionTypesEditor } = useSessionTypesEditor();
  const {
    show: showCancellationEditor,
    close: closeCancellationEditor,
    open: openCancellationEditor,
  } = useToggleModal(false);
  const {
    show: showAvailabilityEditor,
    close: closeAvailabilityEditor,
    open: openAvailabilityEditor,
  } = useToggleModal(false);

  const { canAccessFeature } = useSubscription();

  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showConnectICalendar, setShowConnectICalendar] = useState(false);

  const currentMobileSettingsScreen = useSelector(
    selectCurrentMobileSettingsScreen,
  );

  const mobileSettingsScreenContent = useMemo(() => {
    if (!isPhonePortrait) {
      return null;
    }

    switch (currentMobileSettingsScreen) {
      case MobileSettingsScreenType.CancellationPolicy:
        return <CancellationPolicyMobileScreen />;
      case MobileSettingsScreenType.SessionTypes:
        return <SessionTypesMobileScreen />;
      case MobileSettingsScreenType.Availability:
        return <AvailabilityMobileScreen />;
      case MobileSettingsScreenType.None:
      default:
        return null;
    }
  }, [currentMobileSettingsScreen, isPhonePortrait]);

  // const onClickConnect = () => {
  //   /**
  //    * example of service level check for subscription
  //    */
  //   if (canAccessFeature("calendar-sync")) {
  //     setShowConnectModal(true);
  //   } else {
  //     //do something if not on complete
  //   }
  // };

  const [connected, setConnected] = useState(
    user?.has_calendar_google_connected,
  );

  const [showSelectCalendarsModal, setShowSelectCalendarsModal] =
    useState(false);

  const [GCals, setGCals] = useState<CalendarEntry[]>([]);

  const selected = useMemo(() => {
    return GCals.filter((el) => !el.hidden);
  }, [GCals]);

  const { setShowGCal, setVisibleCalendarIds } = useBlockedIntervals();

  const [showAllCalendars, setShowAllCalendars] = useState(false);
  const [displayAllGCals, setDisplayAllGCals] = useState(true);

  // useEffect(() => {
  //   setConnected(user?.has_calendar_google_connected);
  // }, [user?.has_calendar_google_connected]);

  useEffect(() => {
    // if (!connected) return;
    getSelectedGCals(practiceOwnerOrchidUserId)
      .then((res) => {
        setGCals(res);
        setVisibleCalendarIds(
          res
            .filter((calendar) => !calendar.hidden)
            .map((calendar) => calendar.calendar_id),
        );
      })
      .catch(console.error);
  }, [setVisibleCalendarIds, practiceOwnerOrchidUserId]);

  const handleSetConnected = useCallback<typeof setConnected>(
    (arg) => {
      setConnected(arg);
      setShowGCal(arg);
    },
    [setShowGCal],
  );

  // TODO: temp solution to show orchid provider name as account name
  const connectedAccounts = useMemo(
    () => (!connected ? [] : [getGCalAccountName(user)]),
    [connected, user],
  );

  const saveCalendarDisplaySettings = async (calendar: CalendarEntry) => {
    try {
      await toggleGCalVisibility({
        calendarId: calendar.calendar_id,
        hidden: !calendar.hidden,
      });
      setGCals((prev) =>
        prev.map((el) =>
          el.calendar_id === calendar.calendar_id
            ? { ...el, hidden: !el.hidden }
            : el,
        ),
      );
      setVisibleCalendarIds((prev) =>
        prev.includes(calendar.calendar_id)
          ? prev.filter((id) => id !== calendar.calendar_id)
          : [...prev, calendar.calendar_id],
      );
    } catch (error) {
      console.error(error);
    }
  };

  const toggleTeamMemberVisibility = (memberId: string) => {
    setVisibleTeamMemberIds((prev) => {
      if (prev.includes(memberId)) {
        return prev.filter((id) => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  return (
    <div
      className={getClasses(isPhonePortrait ? styles.phone : styles.desktop)}
    >
      {isPhonePortrait ? (
        <>
          <div className={styles["title"]}>Orchid Calendar</div>
          <TimeRangeSection />
        </>
      ) : (
        <MiniCalendar analyticsPageName={PRO_CALENDAR_ANALYTICS_PAGE_NAME} />
      )}

      {/* settings */}
      <div className={styles["settings"]}>
        <Section title={"Orchid Calendar"} isToggle defaultExpanded={true}>
          <div className={styles["dropdown-toggle"]}>
            <AppointmentsToggle
              analyticsPageName={PRO_CALENDAR_ANALYTICS_PAGE_NAME}
            />
          </div>
          <div className={styles["dropdown-toggle"]}>
            <GroupSessionsToggle
              analyticsPageName={PRO_CALENDAR_ANALYTICS_PAGE_NAME}
            />
          </div>
          <div className={styles["dropdown-toggle"]}>
            <BlockedIntervalsToggle
              analyticsPageName={PRO_CALENDAR_ANALYTICS_PAGE_NAME}
            />
          </div>
          <div className={styles["dropdown-toggle"]}>
            <CancelledSessionsToggle
              analyticsPageName={PRO_CALENDAR_ANALYTICS_PAGE_NAME}
            />
          </div>
        </Section>
  {(user?.sub !== "9099c99b-ff90-4e39-8286-4e62cfb763cc" &&
    user?.sub !== "b02d40d3-446d-4e54-a48d-f2c27cfb566a" &&
    user?.sub !== "4b6c2e07-7cd0-4bc7-b383-87c18c353915" &&
    user?.sub !== "d2fdbb95-918a-4297-b4e7-d28fd12ded9e" &&
    user?.sub !== "4b548507-a212-4fe6-999a-61bb0b8c3bd0" &&
    user?.sub !== "bb45c521-3a85-4457-bba3-8cb1a45406a7" &&
    user?.sub !== "1d3f0961-b168-4091-80d8-1f2d9a525a36" &&
    user?.sub !== "f90fa3cd-c65e-47d6-8bdd-ebee767e0dcd" &&
    user?.sub !== "b359c8e0-c78a-4eb2-820b-a7ddbbb8ecb8" 
    ) &&      <Section defaultExpanded={true} title={"Team Members"} isToggle>
          {teamMembers.length > 0 ? (
            teamMembers
              .filter((member) => member.role !== "PRACTICE_SCHEDULER")
              .map((member) => {
                const isSelected = visibleTeamMemberIds.includes(
                  member.orchid_user_id,
                );
                return (
                  <div key={member.id} className={styles["dropdown-toggle"]}>
                    <CheckboxDropdown
                      Label={`${member.first_name || ""} ${
                        member.last_name || ""
                      } (${formatRoleName(member.role)})`.trim()}
                      onClick={() => {
                        toggleTeamMemberVisibility(member.orchid_user_id);
                      }}
                      Checkbox={
                        <ColorCheckbox
                          color={isSelected ? "var(--white)" : "none"}
                          backgroundColor={
                            isSelected ? "var(--primary)" : "none"
                          }
                          stroke={
                            isSelected ? "var(--primary)" : "var(--gray02)"
                          }
                        />
                      }
                    />
                  </div>
                );
              })
          ) : (
            <div className={styles["dropdown-item"]}>
              <div className={styles["no-team-members"]}>
                No team members found
              </div>
            </div>
          )}
        </Section>}
        {canAccessFeatureByRole("calendar-sync") && (
          <Section title={"Other Calendars"} isToggle defaultExpanded={true}>
            {
              // !connected &&
              GCals.length === 0 ? (
                <div className={styles["dropdown-toggle"]}>
                  <Button
                    disabled={!canAccessFeature("calendar-sync")}
                    variant={"link"}
                    onClick={() => {
                      track(
                        PRO_CALENDAR_ANALYTICS_PAGE_NAME,
                        [
                          ANALYTICS_COMPONENT_NAME,
                          "connect-google-calendar-link",
                        ],
                        "click",
                      );
                      setShowConnectModal(true);
                    }}
                    className={styles["add-calendar-button"]}
                  >
                    <OutlinePlus16 />
                    Sync Google Calendar
                  </Button>
                </div>
              ) : (
                // <div className={styles["dropdown-item"]}>
                //   <GCalToggle />
                //   <Button
                //     className={"py-0"}
                //     variant={"link"}
                //     onClick={() => {
                //       track(
                //         PRO_CALENDAR_ANALYTICS_PAGE_NAME,
                //         [ANALYTICS_COMPONENT_NAME, "edit-connected-apps-button"],
                //         "click",
                //       );
                //       setShowConnectModal(true);
                //     }}
                //   >
                //     Edit
                //   </Button>
                // </div>

                <>
                  <div className={styles["dropdown-toggle"]}>
                    <CheckboxDropdown
                      Label={"Google Calendar"}
                      onClick={() => {
                        setDisplayAllGCals(!displayAllGCals);

                        GCals.forEach((el) => {
                          saveCalendarDisplaySettings(el);
                        });
                      }}
                      Checkbox={
                        <ColorCheckbox
                          color={displayAllGCals ? "var(--white)" : "none"}
                          backgroundColor={
                            displayAllGCals ? "var(--primary)" : "none"
                          }
                          stroke={
                            displayAllGCals ? "var(--primary)" : "var(--gray02)"
                          }
                        />
                      }
                    />
                  </div>

                  <div className={"ml-4"}>
                    {GCals.slice(0, showAllCalendars ? GCals.length : 2).map(
                      (el) => {
                        const isShown = selected.some(
                          (selectedEl) =>
                            selectedEl.calendar_id === el.calendar_id,
                        );
                        return (
                          <div
                            key={el.calendar_id}
                            className={styles["dropdown-toggle"]}
                          >
                            <CheckboxDropdown
                              Label={el.summary || el.calendar_id}
                              onClick={() => {
                                saveCalendarDisplaySettings(el);
                              }}
                              Checkbox={
                                <ColorCheckbox
                                  color={isShown ? "var(--white)" : "none"}
                                  backgroundColor={
                                    isShown ? "var(--primary)" : "none"
                                  }
                                  stroke={
                                    isShown ? "var(--primary)" : "var(--gray02)"
                                  }
                                />
                              }
                            />
                          </div>
                        );
                      },
                    )}
                    {GCals.length > 2 && (
                      <div className={styles["dropdown-toggle"]}>
                        <Button
                          variant={"link"}
                          onClick={() => setShowAllCalendars(!showAllCalendars)}
                          className={styles["show-all-calendar-button"]}
                        >
                          {showAllCalendars ? "Show Less" : "Show All"}
                        </Button>
                      </div>
                    )}
                  </div>
                </>
              )
            }
            <div className={styles["dropdown-toggle"]}>
              <Button
                disabled={!canAccessFeature("calendar-sync")}
                variant={"link"}
                onClick={() => {
                  track(
                    PRO_CALENDAR_ANALYTICS_PAGE_NAME,
                    [
                      ANALYTICS_COMPONENT_NAME,
                      "sync-with-outlook-and-ical-link",
                    ],
                    "click",
                  );
                  setShowConnectICalendar(true);
                }}
                className={styles["add-calendar-button"]}
              >
                <OutlinePlus16 />
                Sync Outlook/iCal
              </Button>
            </div>
            <div className={styles["dropdown-toggle"]}>
              <Button
                variant={"link"}
                onClick={() => {
                  navigate(
                    "/pro-dashboard/settings/integrations?tab=calendar",
                  );
                }}
                className={styles["add-calendar-button"]}
              >
                <OutlinePlus16 />
                Sync other Calendars
              </Button>
            </div>
          </Section>
        )}

        {canAccessFeatureByRole("edit-scheduling") && (
          <Section defaultExpanded={true} title={"Scheduling"} isToggle>
            <div className={styles["dropdown-item"]}>
              <CheckboxDropdown
                Label={messages.myAvailability}
                onClick={() => {
                  track(
                    PRO_CALENDAR_ANALYTICS_PAGE_NAME,
                    [ANALYTICS_COMPONENT_NAME, "availability-button"],
                    "click",
                  );
                  if (isPhonePortrait) {
                    dispatch(showAvailabilityScreen());
                  } else {
                    openAvailabilityEditor();
                  }
                }}
              />
            </div>
            <ScheduleEditorContainer
              show={showAvailabilityEditor}
              onHide={closeAvailabilityEditor}
              analyticsPageName={PRO_CALENDAR_ANALYTICS_PAGE_NAME}
            />
            <div className={styles["dropdown-item"]}>
              <CheckboxDropdown
                Label={messages.mySessionTypes}
                onClick={() => {
                  track(
                    PRO_CALENDAR_ANALYTICS_PAGE_NAME,
                    [
                      ANALYTICS_COMPONENT_NAME,
                      "edit-1-to-1-session-types-link",
                    ],
                    "click",
                  );
                  if (isPhonePortrait) {
                    dispatch(showSessionTypesScreen());
                  } else {
                    openSessionTypesEditor();
                  }
                }}
              />
            </div>
            <div className={styles["dropdown-item"]}>
              <CheckboxDropdown
                Label={"Cancellation Policy"}
                onClick={() => {
                  track(
                    PRO_CALENDAR_ANALYTICS_PAGE_NAME,
                    [
                      ANALYTICS_COMPONENT_NAME,
                      "update-cancellation-policy-link",
                    ],
                    "click",
                  );
                  if (isPhonePortrait) {
                    dispatch(showCancellationPolicyScreen());
                  } else {
                    openCancellationEditor();
                  }
                }}
              />
            </div>
            <CancellationPolicyManager
              show={showCancellationEditor}
              onHide={closeCancellationEditor}
              onCancel={closeCancellationEditor}
              onApply={closeCancellationEditor}
              analyticsPageName={PRO_CALENDAR_ANALYTICS_PAGE_NAME}
            />
          </Section>
        )}
      </div>
      {showSelectCalendarsModal && (
        <SelectGCalsModal
          showSelectCalendarsModal={showSelectCalendarsModal}
          setShowSelectCalendarsModal={setShowSelectCalendarsModal}
          analyticsPageName={PRO_CALENDAR_ANALYTICS_PAGE_NAME}
          initialSyncedCalendars={GCals}
          setGCals={setGCals}
          setVisibleCalendarIds={setVisibleCalendarIds}
        />
      )}
      {showConnectModal && (
        <GoogleCalendar
          connected={connected}
          setConnected={handleSetConnected}
          showConnectModal={showConnectModal}
          setShowConnectModal={setShowConnectModal}
          connectedAccounts={connectedAccounts}
          analyticsPageName={PRO_CALENDAR_ANALYTICS_PAGE_NAME}
          setGCals={setGCals}
          setShowSelectCalendarsModal={setShowSelectCalendarsModal}
        />
      )}
      <ICalCalendar
        showConnectICalendar={showConnectICalendar}
        setShowConnectICalendar={setShowConnectICalendar}
        analyticsPageName={PRO_CALENDAR_ANALYTICS_PAGE_NAME}
      />

      <SidesheetContainer />
      {mobileSettingsScreenContent}
    </div>
  );
};

export default CalendarSidebar;
